import { httpGet } from 'utils';
import { PortfolioBalance } from 'types/DomainTypes';
import { valueWeightedPriceChange } from 'helpers/portfolioReturns';
import { formatCurrency, formatPercentage } from '../../../AIChat/utils/formatters';

interface PortfolioBalancesResponse {
  data: PortfolioBalance[];
}

interface PortalHolding {
  lwin18: string;
  wineName: string;
  vintage: number;
  region: string;
  cultWinesAllocationRegion: string;
  unit: string;
  qty: number;
  qtyOwned: number;
  totalValue: number;
  totalCost: number;
  pnl: number;
  pnlPercent: number;
  marketPrice: number;
  avgCostPerUnit: number;
  changePercent1Month: number;
  changePercent3Months: number;
  changePercent6Months: number;
  changePercent12Months: number;
  wa: number | null;
  vinous: string | null;
  grade: number | null;
  fairValueScore: number | null;
  liquidityScore: number | null;
  normalizedCriticScore: number | null;
  totalBtls: number;
}

interface PortalCurrentHoldingsResponse {
  data: PortalHolding[];
  count: number;
}

interface RegionBreakdown {
  region: string;
  holdingsCount: number;
  totalValue: number;
  totalCost: number;
  pl: number;
  plPercent: number;
}

export const buildPortfolioContext = async (): Promise<string> => {
  // Fetch both holdings and balance data in parallel
  const [holdingsResponse, balanceResponse] = await Promise.all([
    httpGet<PortalCurrentHoldingsResponse, { portfolioId: number }>('portal/PortalCurrentHoldings', {
      portfolioId: -1,
    }),
    httpGet<PortfolioBalancesResponse>('portal/PortfolioStatements'),
  ]);

  const lines: string[] = [];

  // Process balance/account data first (this is what shows in the portfolio selector)
  // The API may return an "All Portfolios" summary row alongside individual portfolios.
  // Filter to only individual portfolios (those with a valid portfolioId) to avoid double-counting.
  const rawBalanceData = balanceResponse?.data || [];
  const individualPortfolios = rawBalanceData.filter(
    (p) => p.portfolioId != null && String(p.portfolioId).trim() !== '',
  );
  const balanceData = individualPortfolios.length > 0 ? individualPortfolios : rawBalanceData;
  if (balanceData.length > 0) {
    const totalPortfolioValue = balanceData.reduce((sum, p) => sum + (p.currentHoldings || 0), 0);
    const totalAvailableBalance = balanceData.reduce((sum, p) => sum + (p.balance || 0), 0);
    const totalCapitalInvested = balanceData.reduce((sum, p) => sum + (p.capitalInvested || 0), 0);
    const totalProfitLoss = balanceData.reduce((sum, p) => sum + (p.profitAndLoss || 0), 0);
    const totalValuation = balanceData.reduce((sum, p) => sum + (p.valuation || 0), 0);

    lines.push('## Account Summary (All Portfolios)');
    lines.push(`- Total Portfolio Value: ${formatCurrency(totalPortfolioValue)}`);
    lines.push(`- Total Available Balance (Cash): ${formatCurrency(totalAvailableBalance)}`);
    lines.push(`- Total Capital Invested: ${formatCurrency(totalCapitalInvested)}`);
    lines.push(`- Total Profit & Loss: ${formatCurrency(totalProfitLoss)}`);
    lines.push(`- Total Valuation: ${formatCurrency(totalValuation)}`);
    lines.push('');

    // Individual portfolio breakdown
    if (balanceData.length > 1) {
      lines.push('## Individual Portfolio Balances');
      balanceData.forEach((p) => {
        lines.push(`### ${p.portfolioName}`);
        lines.push(`- Portfolio Value: ${formatCurrency(p.currentHoldings || 0)}`);
        lines.push(`- Available Balance (Cash): ${formatCurrency(p.balance || 0)}`);
        lines.push(`- Capital Invested: ${formatCurrency(p.capitalInvested || 0)}`);
        const plPct = formatPercentage(p.profitAndLossPct || 0);
        lines.push(`- Profit & Loss: ${formatCurrency(p.profitAndLoss || 0)} (${plPct})`);
      });
      lines.push('');
    }
  }

  // Process holdings data
  if (!holdingsResponse.data || holdingsResponse.data.length === 0) {
    if (lines.length === 0) {
      return 'No portfolio data available.';
    }
    lines.push('## Holdings');
    lines.push('No wine holdings data available.');
    return lines.join('\n');
  }

  const holdings = holdingsResponse.data;

  // Holdings Summary
  const totalHoldings = holdings.length;
  const totalCost = holdings.reduce((sum, h) => sum + (h.totalCost || 0), 0);
  const totalValue = holdings.reduce((sum, h) => sum + (h.totalValue || 0), 0);
  const totalPL = holdings.reduce((sum, h) => sum + (h.pnl || 0), 0);
  const totalPLPercent = totalCost > 0 ? (totalPL / totalCost) * 100 : 0;

  lines.push('## Wine Holdings Summary');
  lines.push(`- Total Holdings: ${totalHoldings} unique wines`);
  lines.push(`- Total Cost Basis: ${formatCurrency(totalCost)}`);
  lines.push(`- Current Market Value: ${formatCurrency(totalValue)}`);
  lines.push(`- Total P&L: ${formatCurrency(totalPL)} (${formatPercentage(totalPLPercent)})`);
  lines.push('');

  // Market price movement of the wines currently held, weighted by how much of the cellar each
  // one represents. This is NOT the portfolio's return: it ignores wines that were sold during
  // the period, credits wines with gains they made before the client bought them, and takes no
  // account of cash, fees or money paid in. Portfolio return lives in the valuation context.
  const avg = (arr: number[]) => (arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

  const priceMove = (changeOf: (h: PortalHolding) => number | null | undefined) =>
    valueWeightedPriceChange(holdings.map((h) => ({ totalValue: h.totalValue, change: changeOf(h) })));

  const move1m = priceMove((h) => h.changePercent1Month);
  const move3m = priceMove((h) => h.changePercent3Months);
  const move6m = priceMove((h) => h.changePercent6Months);
  const move12m = priceMove((h) => h.changePercent12Months);
  const fmtMove = (v: number | null) => (v === null ? 'no price history' : `${v.toFixed(2)}%`);

  lines.push('## Market Price Movement of Wines Currently Held');
  lines.push('Value-weighted price movement of the wines in the cellar today. This is NOT the portfolio return -');
  lines.push('it excludes wines sold during the period, counts gains made before the client bought a wine, and');
  lines.push('ignores cash, fees and contributions. For performance questions use the valuation context.');
  lines.push(`- Wine prices, last 1 month: ${fmtMove(move1m)}`);
  lines.push(`- Wine prices, last 3 months: ${fmtMove(move3m)}`);
  lines.push(`- Wine prices, last 6 months: ${fmtMove(move6m)}`);
  lines.push(`- Wine prices, last 12 months: ${fmtMove(move12m)}`);
  lines.push('');

  // Average scores
  const withGrade = holdings.filter((h) => h.grade !== null);
  const withFV = holdings.filter((h) => h.fairValueScore !== null);
  const withLiq = holdings.filter((h) => h.liquidityScore !== null);

  const avgGrade = withGrade.length > 0 ? avg(withGrade.map((h) => h.grade as number)) : null;
  const avgFairValue = withFV.length > 0 ? avg(withFV.map((h) => h.fairValueScore as number)) : null;
  const avgLiquidity = withLiq.length > 0 ? avg(withLiq.map((h) => h.liquidityScore as number)) : null;

  lines.push('## Average Scores');
  lines.push(`- Average Grade: ${avgGrade !== null ? `${avgGrade.toFixed(1)}/10` : 'N/A'}`);
  lines.push(`- Fair Value Score: ${avgFairValue !== null ? `${avgFairValue.toFixed(0)}/100` : 'N/A'}`);
  lines.push(`- Liquidity Score: ${avgLiquidity !== null ? `${avgLiquidity.toFixed(0)}/100` : 'N/A'}`);
  lines.push('');

  // Regional breakdown
  const regionMap = new Map<string, PortalHolding[]>();
  holdings.forEach((h) => {
    const region = h.cultWinesAllocationRegion || h.region || 'Unknown';
    const existing = regionMap.get(region);
    if (existing) {
      existing.push(h);
    } else {
      regionMap.set(region, [h]);
    }
  });

  const regionBreakdown: RegionBreakdown[] = Array.from(regionMap.entries())
    .map(([region, items]) => {
      const regionValue = items.reduce((sum, h) => sum + (h.totalValue || 0), 0);
      const regionCost = items.reduce((sum, h) => sum + (h.totalCost || 0), 0);
      const regionPL = items.reduce((sum, h) => sum + (h.pnl || 0), 0);
      return {
        region,
        holdingsCount: items.length,
        totalValue: regionValue,
        totalCost: regionCost,
        pl: regionPL,
        plPercent: regionCost > 0 ? (regionPL / regionCost) * 100 : 0,
      };
    })
    .sort((a, b) => b.totalValue - a.totalValue);

  lines.push('## Regional Breakdown');
  regionBreakdown.forEach((r) => {
    lines.push(
      `- ${r.region}: ${r.holdingsCount} wines, ${formatCurrency(r.totalValue)} (${formatPercentage(r.plPercent)} P&L)`,
    );
  });
  lines.push('');

  // Top 5 performers
  const sorted = [...holdings].sort((a, b) => (b.pnlPercent || 0) - (a.pnlPercent || 0));
  const top5 = sorted.slice(0, 5);
  const bottom5 = sorted.slice(-5).reverse();

  lines.push('## Top 5 Performers');
  top5.forEach((w, i) => {
    lines.push(
      `${i + 1}. ${w.wineName} ${w.vintage} - ${formatPercentage(w.pnlPercent * 100)} (Grade: ${w.grade ?? 'N/A'})`,
    );
  });
  lines.push('');

  lines.push('## Bottom 5 Performers');
  bottom5.forEach((w, i) => {
    lines.push(
      `${i + 1}. ${w.wineName} ${w.vintage} - ${formatPercentage(w.pnlPercent * 100)} (Grade: ${w.grade ?? 'N/A'})`,
    );
  });
  lines.push('');

  // Complete holdings list
  lines.push('## Complete Holdings List');
  holdings.forEach((h) => {
    const grade = h.grade !== null ? `Grade:${h.grade}` : '';
    const fv = h.fairValueScore !== null ? `FV:${h.fairValueScore}` : '';
    const liq = h.liquidityScore !== null ? `Liq:${h.liquidityScore}` : '';
    lines.push(
      `- ${h.wineName} ${h.vintage} | ${h.unit} x${h.qty} | Cost:${formatCurrency(
        h.totalCost,
      )} | Value:${formatCurrency(h.totalValue)} | P&L:${formatPercentage(
        h.pnlPercent * 100,
      )} | ${grade} ${fv} ${liq}`.trim(),
    );
  });

  return lines.join('\n');
};
